// Ekran 08 — Filo API istemcisi.

import { api } from '../../lib/api'

export type AracDurumu = 'hazir' | 'seferde' | 'sahada' | 'bakimda'
export type DorseUygunlugu = 'kati' | 'sivi'
export type SeferDurumu = 'planlandi' | 'yolda' | 'sahada' | 'tamamlandi' | 'iptal'
export type BakimDurumu = 'planlandi' | 'serviste' | 'tamamlandi'
export type CezaDurumu = 'bekliyor' | 'odendi' | 'itiraz'
export type SahipTipi = 'arac' | 'dorse' | 'surucu'
export type VadeDurumu = 'veriYok' | 'gecerli' | 'yaklasiyor' | 'gecmis'
export type DolumBandi = 'yok' | 'saglikli' | 'izle' | 'kritik'

export type Vade = { tarih: string | null; gun: number | null; durum: VadeDurumu }

/** Kayıtlardan türetiliyor — saklanmıyor (bkz. docs/kararlar-ek.md). */
export type AylikOzet = {
  litre: number | null
  yakitTl: number | null
  bakimTl: number | null
  lastikTl: number | null
  km: number | null
  tlKm: number | null
  l100: number | null
}

export type Arac = {
  id: string
  plaka: string
  tip: string | null
  model: string | null
  sayacKm: number | null
  surucuId: string | null
  surucuAd: string | null
  dorseId: string | null
  dorsePlaka: string | null
  durum: AracDurumu
  ay: AylikOzet
  evrak: Vade
  evrakSayisi: number
}

export type Dorse = {
  id: string
  plaka: string
  tip: string | null
  hacim: number | null
  uygunluk: DorseUygunlugu
  muayene: Vade
  aracId: string | null
  aracPlaka: string | null
}

export type Surucu = {
  id: string
  ad: string
  tel: string | null
  puan: number | null
  aracId: string | null
  aracPlaka: string | null
  belge: Vade
  belgeSayisi: number
  acikCeza: number
}

export type Evrak = {
  id: string
  sahipTip: SahipTipi
  sahipId: string
  sahipAd: string
  tur: string
  vade: Vade
}

export type Ceza = {
  id: string
  tarih: string
  aracId: string | null
  aracPlaka: string | null
  surucuId: string | null
  surucuAd: string | null
  madde: string | null
  tutar: number | null
  durum: CezaDurumu
}

export type Dolum = {
  id: string
  zaman: string
  aracId: string
  aracPlaka: string
  surucuId: string | null
  surucuAd: string | null
  litre: number
  tutar: number | null
  birimFiyat: number | null
  sayacKm: number | null
  istasyon: string | null
  aralikKm: number | null
  aralikL100: number | null
  sapma: number | null
  bant: DolumBandi
}

export type BakimEki = {
  id: string
  dosyaAdi: string
  mime: string | null
  boyut: number | null
  yol: string
}

export type Bakim = {
  id: string
  tarih: string
  sahipTip: SahipTipi
  sahipId: string
  sahipPlaka: string
  tur: string | null
  aciklama: string | null
  servis: string | null
  sayacKm: number | null
  tutar: number | null
  durum: BakimDurumu
  ekler: BakimEki[]
}

export type Sefer = {
  id: string
  prgNo: string
  tarih: string
  saat: string | null
  aracId: string | null
  aracPlaka: string | null
  surucuId: string | null
  surucuAd: string | null
  dorseId: string | null
  dorsePlaka: string | null
  cariId: string | null
  cariUnvan: string | null
  stokKartiId: string | null
  stokKod: string | null
  stokAd: string | null
  /** K19: hijyen bayraklı kart — programda rozetle işaretleniyor. */
  stokHij: boolean
  /** K35: sıvı yük tanker ister; satırda "· sıvı / · katı" olarak yazılıyor. */
  stokSividir: boolean
  planT: number | null
  gercekT: number | null
  durum: SeferDurumu
  /** K36: Tamamlandı'da açılan mal kabul taslağı. */
  lotId: string | null
  lotBelgeNo: string | null
  lotDurum: string | null
}

export type FiloOzeti = {
  aracSayisi: number
  dorseSayisi: number
  surucuSayisi: number
  bugunSeferToplam: number
  bugunSeferTamamlanan: number
  bugunTasinanT: number | null
  ortalamaTlKm: number | null
  evrakUyarisi: number
}

export type FiloSayaclari = {
  arac: number
  dorse: number
  surucu: number
  yakit: number
  bakim: number
  ceza: number
}

export type SeferDurumSonucu = {
  sefer: Sefer
  lotId: string | null
  lotBelgeNo: string | null
  taslakYeni: boolean
  mesaj: string
}

export type BakimDurumSonucu = {
  bakim: Bakim
  tlKmOncesi: number | null
  tlKmSonrasi: number | null
  mesaj: string
}

export type UygunlukSonucu = { uygun: boolean; ihlaller: string[] }

export const filo = {
  ozet: () => api<FiloOzeti>('/api/filo/ozet'),
  sayaclar: () => api<FiloSayaclari>('/api/filo/sayaclar'),

  araclar: () => api<Arac[]>('/api/araclar'),
  aracKaydet: (id: string | null, govde: Partial<Arac>) =>
    api<Arac>(id ? `/api/araclar/${id}` : '/api/araclar', { yontem: id ? 'PUT' : 'POST', govde }),
  atama: (id: string, govde: { surucuId: string | null; dorseId: string | null }) =>
    api<Arac>(`/api/araclar/${id}/atama`, { yontem: 'PUT', govde }),

  dorseler: () => api<Dorse[]>('/api/dorseler'),
  dorseKaydet: (id: string | null, govde: Record<string, unknown>) =>
    api<Dorse>(id ? `/api/dorseler/${id}` : '/api/dorseler', { yontem: id ? 'PUT' : 'POST', govde }),

  suruculer: () => api<Surucu[]>('/api/suruculer'),
  surucuKaydet: (id: string | null, govde: Record<string, unknown>) =>
    api<Surucu>(id ? `/api/suruculer/${id}` : '/api/suruculer', { yontem: id ? 'PUT' : 'POST', govde }),

  evraklar: () => api<Evrak[]>('/api/evraklar'),
  cezalar: () => api<Ceza[]>('/api/cezalar'),
  cezaKaydet: (id: string, govde: Record<string, unknown>) =>
    api<Ceza>(`/api/cezalar/${id}`, { yontem: 'PUT', govde }),

  dolumlar: () => api<Dolum[]>('/api/dolumlar'),
  dolumEkle: (govde: Record<string, unknown>) =>
    api<Dolum>('/api/dolumlar', { yontem: 'POST', govde }),

  bakimlar: () => api<Bakim[]>('/api/bakimlar'),
  bakimEkle: (govde: Record<string, unknown>) =>
    api<Bakim>('/api/bakimlar', { yontem: 'POST', govde }),
  bakimGuncelle: (id: string, govde: Record<string, unknown>) =>
    api<BakimDurumSonucu>(`/api/bakimlar/${id}`, { yontem: 'PUT', govde }),

  seferler: (tarih?: string) =>
    api<Sefer[]>(`/api/sefer${tarih ? `?tarih=${tarih}` : ''}`),
  seferEkle: (govde: Record<string, unknown>) =>
    api<Sefer>('/api/sefer', { yontem: 'POST', govde }),
  seferDurum: (id: string, govde: { durum: SeferDurumu; gercekT: number | null }) =>
    api<SeferDurumSonucu>(`/api/sefer/${id}/durum`, { yontem: 'PUT', govde }),
  /** Drawer'ın canlı K35 ön denetimi — kayıt açmaz. */
  uygunluk: (govde: Record<string, unknown>) =>
    api<UygunlukSonucu>('/api/sefer/uygunluk', { yontem: 'POST', govde }),
}

/** Bakım eki yükler. Multipart olduğu için api() yardımcısından geçmiyor. */
export async function ekYukle(bakimId: string, dosya: File, jeton: string | null) {
  const govde = new FormData()
  govde.append('dosya', dosya)

  const yanit = await fetch(`/api/bakimlar/${bakimId}/ekler`, {
    method: 'POST',
    headers: jeton ? { Authorization: `Bearer ${jeton}` } : {},
    body: govde,
  })

  if (!yanit.ok) {
    const govdeJson = await yanit.json().catch(() => null)
    throw new Error(govdeJson?.hata?.mesaj ?? `Ek yüklenemedi (${yanit.status})`)
  }
  return (await yanit.json()) as BakimEki
}
